import { uiAnimate, UIAnimation } from './animate';
import { uiGetContext, uiTrackAttach } from './attach';
import { $el } from './elements';
import { UIPoint, UIRect, UIRelPos } from './types';
import { UIViewSpec } from './view';

export interface UIOpenPopup {
    close: (close_parent?: boolean) => void;
    _context: HTMLElement;
    _elem: HTMLElement;
    _children: UIOpenPopup[];
}

declare global {
    interface Element {
        ui_open_popup?: UIOpenPopup;
    }
}

export interface UIPopupOptions {
    context?: UIViewSpec;
    anchor?: UIViewSpec | UIPoint;    //Reference node by default
    position: UIRelPos;
    offset?: number;
    match_size?: boolean;
    adjust_size?: boolean;
    fix_size?: boolean;
    can_flip?: boolean;
    animate?: boolean;
    parent?: UIViewSpec;
    prevent_blur?: boolean;

    capture_mouse?: boolean;
    close_on_click?: boolean;
    close_on_wheel?: boolean;
    on_close?: () => void;
}

//anchor rect and contain rect are in context coordinates
function doPosition(popup: HTMLElement, anchor: UIRect, contain: UIRect, options: UIPopupOptions) {
    const offset = options.offset || 0;
    const pos = options.position;

    let edge_min_pos: number;
    let edge_max_pos: number;
    let cross_min_pos: number;
    let cross_max_pos: number;

    let anchor_edge_start: number; // the start of the edge along its dimension
    let anchor_edge_end: number;
    let anchor_cross_start: number;
    let anchor_cross_end: number;

    let before: boolean;

    let edgeMinStyle: 'minHeight' | 'minWidth';
    let edgeMaxStyle: 'maxHeight' | 'maxWidth';
    let crossMaxStyle: 'maxHeight' | 'maxWidth';
    let edgePosStyleStart: 'top' | 'left';
    let edgePosStyleEnd: 'bottom' | 'right';
    let crossPosStyle: 'top' | 'bottom' | 'left' | 'right';
    let popupEdgeSizeProperty: 'offsetWidth' | 'offsetHeight';
    let popupCrossSizeProperty: 'offsetHeight' | 'offsetWidth';

    if(pos & UIRelPos.Left || pos & UIRelPos.Right) {
        edge_min_pos = contain[1];
        edge_max_pos = contain[3];
        cross_min_pos = contain[0];
        cross_max_pos = contain[2];

        anchor_edge_start = anchor[1];
        anchor_edge_end = anchor[3];
        anchor_cross_start = anchor[0] - offset;
        anchor_cross_end = anchor[2] + offset;

        if(pos & UIRelPos.Left) {
            popup.classList.add('a-left');
            crossPosStyle = 'right';
            before = true;
        } else {
            popup.classList.add('a-right');
            crossPosStyle = 'left';
            before = false;
        }

        edgeMinStyle = 'minHeight';
        edgeMaxStyle = 'maxHeight';
        crossMaxStyle = 'maxWidth';
        popupEdgeSizeProperty = 'offsetHeight';
        popupCrossSizeProperty = 'offsetWidth';
        edgePosStyleStart = 'top';
        edgePosStyleEnd = 'bottom';
    } else {
        edge_min_pos = contain[0];
        edge_max_pos = contain[2];
        cross_min_pos = contain[1];
        cross_max_pos = contain[3];

        anchor_edge_start = anchor[0];
        anchor_edge_end = anchor[2];
        anchor_cross_start = anchor[1] - offset;
        anchor_cross_end = anchor[3] + offset;

        if(pos & UIRelPos.Above) {
            popup.classList.add('a-above');
            crossPosStyle = 'bottom';
            before = true;
        } else {
            popup.classList.add('a-below');
            crossPosStyle = 'top';
            before = false;
        }

        edgeMinStyle = 'minWidth';
        edgeMaxStyle = 'maxWidth';
        crossMaxStyle = 'maxHeight';
        popupEdgeSizeProperty = 'offsetWidth';
        popupCrossSizeProperty = 'offsetHeight';
        edgePosStyleStart = 'left';
        edgePosStyleEnd = 'right';
    }

    //first, set min and max sizes
    if(options.match_size)
        popup.style[edgeMinStyle] = (anchor_edge_end - anchor_edge_start) + 'px';

    if(options.adjust_size) {
        popup.style[edgeMaxStyle] = (edge_max_pos - edge_min_pos) + 'px';

        if(options.can_flip) {
            popup.style[crossMaxStyle] = Math.max(anchor_cross_start - cross_min_pos, cross_max_pos - anchor_cross_end) + 'px';
        } else if(before) {
            popup.style[crossMaxStyle] = (anchor_cross_start - cross_min_pos) + 'px';
        } else {
            popup.style[crossMaxStyle] = (cross_max_pos - anchor_cross_end) + 'px';
        }
    }

    //compute sizes after setting limits
    const popup_edge_size = popup[popupEdgeSizeProperty];
    const popup_cross_size = popup[popupCrossSizeProperty];

    //calculate the edge position
    let popup_edge_pos: number;

    if(pos & UIRelPos.Start)
        popup_edge_pos = anchor_edge_start;
    else if(pos & UIRelPos.End)
        popup_edge_pos = anchor_edge_end - popup_edge_size;
    else
        popup_edge_pos = (anchor_edge_end + anchor_edge_start - popup_edge_size) / 2;

    if(popup_edge_pos < edge_min_pos)
        popup_edge_pos = edge_min_pos;
    else if(popup_edge_pos + popup_edge_size > edge_max_pos)
        popup_edge_pos = edge_max_pos - popup_edge_size;

    let popup_cross_pos: number;
    let popup_cross_end = false;

    //calculate the cross position
    if(before) {
        popup_cross_pos = anchor_cross_start;
        popup_cross_end = true;

        if(options.can_flip && popup_cross_pos - popup_cross_size < cross_min_pos - 1) {
            if(crossPosStyle == 'bottom')
                crossPosStyle = 'top';
            else if(crossPosStyle == 'right')
                crossPosStyle = 'left';

            popup_cross_pos = anchor_cross_end;
            popup_cross_end = false;
        }
    } else {
        popup_cross_pos = anchor_cross_end;
        popup_cross_end = false;

        if(options.can_flip && popup_cross_pos + popup_cross_size > cross_max_pos + 1) {
            if(crossPosStyle == 'top')
                crossPosStyle = 'bottom';
            else if(crossPosStyle == 'left')
                crossPosStyle = 'right';

            popup_cross_pos = anchor_cross_start;
            popup_cross_end = true;
        }
    }

    if(options.adjust_size) {
        // TODO: doing it just in general messes with UITooltip, which has elements outside of its size. A solution will be to add the overflow to some internal element
        //which actually contains the scrollable content, or maybe adding the arrow inside the tooltip UIPopup
        if(popup.scrollHeight > popup.offsetHeight)
            popup.style.overflowY = 'auto';
        if(popup.scrollWidth > popup.offsetWidth)
            popup.style.overflowX = 'auto';
    }

    if(pos & UIRelPos.End) {
        popup.style[edgePosStyleEnd] = 'calc(100% - ' + (popup_edge_pos + popup_edge_size) + 'px)';
    } else {
        popup.style[edgePosStyleStart] = popup_edge_pos + 'px';
    }

    if(popup_cross_end)
        popup.style[crossPosStyle] = 'calc(100% - ' + popup_cross_pos + 'px)';
    else
        popup.style[crossPosStyle] = popup_cross_pos + 'px';
}

export function uiShowPopup(popup: UIViewSpec, ref: UIViewSpec, options: UIPopupOptions) {
    const popup_elem = popup.ui_elem;
    let open_popup = popup_elem.ui_open_popup;
    let context: HTMLElement | undefined;
    let children: UIOpenPopup[];
    let close_animation: UIAnimation | undefined;

    if(open_popup) {
        context = open_popup._context;
        children = open_popup._children;
    } else {
        context = options.context?.ui_elem || uiGetContext(ref);
        if(!context) //or already has popup?
            throw new Error("Can't show popup without context");
        children = [];

        const parent = options.parent?.ui_elem.ui_open_popup;
        const on_close = options.on_close;

        popup_elem.ui_open_popup = open_popup = {
            close: (close_parent?: boolean) => {
                if(close_parent && parent) {
                    parent.close(true);
                    return;
                }

                context!.removeEventListener('mousedown', close_cb, true);
                context!.removeEventListener('wheel', close_cb, true);

                if(close_animation) {
                    uiAnimate([close_animation], () => { popup_elem.remove(); });
                } else {
                    popup_elem.remove();
                }

                backdrop?.remove();
                on_close?.();

                popup_elem.ui_open_popup = undefined;

                uiTrackAttach();
                for(const c of children) {
                    c.close();
                }
            },
            _context: context,
            _elem: popup_elem,
            _children: children
        }

        const close_cb = (ev: MouseEvent) => {
            const target = ev.target as HTMLElement;
            if(ev.button == 2) return;
            if(popup_elem.contains(target)) return;
            for(const c of children) {
                if(c._elem.contains(target)) return;
            }
            open_popup!.close();

            if(options.prevent_blur) {
                ev.preventDefault();
                ev.stopPropagation();
            }
        }

        if(parent)
            parent._children.push(open_popup);

        let backdrop: HTMLElement | undefined;

        if(options.capture_mouse) {
            context.appendChild(backdrop = $el.DIV('ui-popup-backdrop'));

            options.close_on_click = true;
            options.close_on_wheel = true;
        }

        if(options.prevent_blur) {
            popup_elem.addEventListener('mousedown', ev => {
                ev.preventDefault();
                ev.stopPropagation();
            });
        }

        if(options.close_on_click)
            context.addEventListener('mousedown', close_cb, true);

        if(options.close_on_wheel)
            context.addEventListener('wheel', close_cb, true);

        popup_elem.classList.add('ui-popup');
        context.ui_add(popup_elem);
    }

    const anchor = options.anchor || ref;
    const context_bounding = context.getBoundingClientRect();

    let anchor_rect: UIRect; //in context coordinates
    if(Array.isArray(anchor)) {
        anchor_rect = [anchor[0], anchor[1], anchor[0], anchor[1]];
    } else {
        const anchor_bounding = anchor.ui_elem.getBoundingClientRect();
        anchor_rect = [anchor_bounding.left - context_bounding.left, anchor_bounding.top - context_bounding.top, anchor_bounding.right - context_bounding.left, anchor_bounding.bottom - context_bounding.top];
    }

    //in context coordinates
    const contain_rect: UIRect = [-context_bounding.left + 10, -context_bounding.top + 10, -context_bounding.left + window.innerWidth - 10, -context_bounding.top + window.innerHeight - 10];

    doPosition(popup_elem, anchor_rect, contain_rect, options);

    if(options.animate) {
        uiAnimate([{ what: popup_elem, animation: 'ui-tooltip-in', duration: 0.15, easing: 'ease-out' }]);
        close_animation = { what: popup_elem, animation: 'ui-tooltip-out', duration: 0.075, easing: 'ease-in' };
    }

    uiTrackAttach();
}

export function uiHidePopup(popup: UIViewSpec, close_parent?: boolean) {
    popup.ui_elem.ui_open_popup?.close(close_parent);
}
